import React, { useCallback, useMemo, useState } from "react";
import { UserCog, UserPlus, Wallet } from "lucide-react";
import BaseSelect from "../../Base/BaseSelect";
import { Service } from "../../../models/ServiceModel";
import { ServicePriority } from "../../../enums/ServiceEnum";
import showToast from "../../../utils/toast";
import AuthorizerQuickAddModal, {
  QuickCreatedAuthorizer,
} from "../../Companies/AuthorizerQuickAddModal";
import RequesterQuickAddModal, {
  QuickCreatedRequester,
} from "../../Companies/RequesterQuickAddModal";
import CostCenterQuickAddModal, {
  QuickCreatedCostCenter,
} from "../../Companies/CostCenterQuickAddModal";
import "../styles/ServiceFormStep3.css";

type DriverOption = { value: number; label: string; status: string; is_available: boolean };
type SimpleOption = { value: number; label: string; company: number; is_active: boolean };

type Props = {
  value: Service;
  onChange: (field: keyof Service, value: any) => void;
  drivers: DriverOption[];
  authorizers: SimpleOption[];
  requesters: SimpleOption[];
  costCenters: SimpleOption[];
  fieldErrors?: Record<string, string>;
};

type FieldWithAddProps = {
  label: string;
  name: string;
  options: { value: string; label: string }[];
  value: string;
  onChange: (e: React.ChangeEvent<HTMLSelectElement>) => void;
  placeholder: string;
  disabled?: boolean;
  onAddNew?: () => void;
  addTitle?: string;
  addLabel?: string;
  addIcon?: React.ReactNode;
};

const FieldWithAdd: React.FC<FieldWithAddProps> = ({
  label,
  name,
  options,
  value,
  onChange,
  placeholder,
  disabled,
  onAddNew,
  addTitle,
  addLabel = "Nuevo",
  addIcon,
}) => (
  <div className="step3-field step3-field--with-add">
    <div className="step3-field__head">
      <span className="step3-field__label">{label}</span>
      {onAddNew && (
        <button
          type="button"
          className="step3-field__add-btn"
          onClick={onAddNew}
          title={addTitle}
        >
          {addIcon}
          {addLabel}
        </button>
      )}
    </div>
    <BaseSelect
      label=""
      name={name}
      preserveCase
      options={options}
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      disabled={disabled}
    />
  </div>
);

const ServiceFormStep3: React.FC<Props> = ({
  value,
  onChange,
  drivers,
  authorizers,
  requesters,
  costCenters,
  fieldErrors,
}) => {
  const [authorizerModalOpen, setAuthorizerModalOpen] = useState(false);
  const [requesterModalOpen, setRequesterModalOpen] = useState(false);
  const [costCenterModalOpen, setCostCenterModalOpen] = useState(false);
  const [extraAuthorizers, setExtraAuthorizers] = useState<SimpleOption[]>([]);
  const [extraRequesters, setExtraRequesters] = useState<SimpleOption[]>([]);
  const [extraCostCenters, setExtraCostCenters] = useState<SimpleOption[]>([]);

  const handleChange = (field: keyof Service) => (e: React.ChangeEvent<HTMLSelectElement>) => {
    const val = e.target.value ? parseInt(e.target.value, 10) : null;
    onChange(field, val);
  };

  const hasCompany = Boolean(value.company);
  const companyId = Number(value.company) || 0;

  const mergedAuthorizers = useMemo(() => {
    const byId = new Map<number, SimpleOption>();
    for (const a of authorizers) byId.set(a.value, a);
    for (const a of extraAuthorizers) byId.set(a.value, a);
    return Array.from(byId.values());
  }, [authorizers, extraAuthorizers]);

  const mergedRequesters = useMemo(() => {
    const byId = new Map<number, SimpleOption>();
    for (const r of requesters) byId.set(r.value, r);
    for (const r of extraRequesters) byId.set(r.value, r);
    return Array.from(byId.values());
  }, [requesters, extraRequesters]);

  const mergedCostCenters = useMemo(() => {
    const byId = new Map<number, SimpleOption>();
    for (const c of costCenters) byId.set(c.value, c);
    for (const c of extraCostCenters) byId.set(c.value, c);
    return Array.from(byId.values());
  }, [costCenters, extraCostCenters]);

  const filteredAuthorizers = mergedAuthorizers.filter(
    (a) => Number(a.company) === Number(value.company) && a.is_active
  );

  const filteredRequesters = mergedRequesters.filter(
    (r) => Number(r.company) === Number(value.company) && r.is_active
  );

  const filteredCostCenters = mergedCostCenters.filter(
    (c) => Number(c.company) === Number(value.company) && c.is_active
  );

  const requireCompany = useCallback((entity: string) => {
    if (!hasCompany) {
      showToast(`Selecciona una empresa en el paso 1 para agregar un ${entity}.`, "error");
      return false;
    }
    return true;
  }, [hasCompany]);

  const handleAuthorizerCreated = useCallback(
    (a: QuickCreatedAuthorizer) => {
      const label = `${a.name} ${a.first_lastname}`.trim() + (a.email ? ` | ${a.email}` : "");
      const newOpt: SimpleOption = {
        value: a.id,
        label,
        company: a.company,
        is_active: true,
      };
      setExtraAuthorizers((prev) => [
        ...prev.filter((x) => x.value !== newOpt.value),
        newOpt,
      ]);
      onChange("authorizer", newOpt.value);
      setAuthorizerModalOpen(false);
    },
    [onChange]
  );

  const handleRequesterCreated = useCallback(
    (r: QuickCreatedRequester) => {
      const label = `${r.name} ${r.first_lastname}`.trim() + (r.email ? ` | ${r.email}` : "");
      const newOpt: SimpleOption = {
        value: r.id,
        label,
        company: r.company,
        is_active: true,
      };
      setExtraRequesters((prev) => [
        ...prev.filter((x) => x.value !== newOpt.value),
        newOpt,
      ]);
      onChange("requester", newOpt.value);
      setRequesterModalOpen(false);
    },
    [onChange]
  );

  const handleCostCenterCreated = useCallback(
    (c: QuickCreatedCostCenter) => {
      const newOpt: SimpleOption = {
        value: c.id,
        label: c.code,
        company: c.company,
        is_active: true,
      };
      setExtraCostCenters((prev) => [
        ...prev.filter((x) => x.value !== newOpt.value),
        newOpt,
      ]);
      onChange("cost_center", newOpt.value);
      setCostCenterModalOpen(false);
    },
    [onChange]
  );

  const globalError = fieldErrors && Object.values(fieldErrors)[0];

  return (
    <div className="service-form-step3">
      <header className="step3-header">
        <div className="step3-header__icon" aria-hidden>
          <UserCog size={26} strokeWidth={2} />
        </div>
        <div className="step3-header__main">
          <div className="step3-header__title-row">
            <h3 className="step3-title">Asignación de recursos</h3>
            <span className="step3-badge">Opcional</span>
          </div>
          <p className="step3-description">
            Conductor, autorizador, solicitante y centro de costo. Puedes dejarlos vacíos y
            completarlos después.
          </p>
        </div>
      </header>

      <div className="step3-panel">
        <div className="step3-fields-grid">
          <div className="step3-field">
            <BaseSelect
              label="Prioridad"
              name="priority"
              preserveCase
              value={String(value.priority ?? ServicePriority.NORMAL)}
              onChange={(e) =>
                onChange("priority", e.target.value || ServicePriority.NORMAL)
              }
              placeholder="Seleccionar prioridad"
              options={[
                { value: ServicePriority.NORMAL, label: "Normal" },
                { value: ServicePriority.VIP, label: "VIP" },
              ]}
            />
          </div>

          <div className="step3-field step3-field--checkbox">
            <label className="step3-purple-toggle">
              <input
                type="checkbox"
                name="style_color"
                checked={value.style_color === "purple"}
                onChange={(e) =>
                  onChange("style_color", e.target.checked ? "purple" : null)
                }
              />
              <span className="step3-purple-toggle__title">Color morado</span>
            </label>
          </div>

          <div className="step3-field">
            <BaseSelect
              label="Conductor"
              name="driver"
              preserveCase
              value={String(value.driver ?? "")}
              onChange={handleChange("driver")}
              placeholder={
                drivers.length === 0
                  ? "Sin conductores disponibles"
                  : "Seleccionar conductor"
              }
              options={drivers.map((d) => ({
                value: String(d.value),
                label: d.label,
              }))}
              disabled={drivers.length === 0}
            />
          </div>

          <FieldWithAdd
            label="Autorizador"
            name="authorizer"
            value={String(value.authorizer ?? "")}
            onChange={handleChange("authorizer")}
            placeholder={
              !hasCompany
                ? "Selecciona empresa en el paso 1"
                : filteredAuthorizers.length === 0
                  ? "Sin autorizadores — usa Nuevo"
                  : "Seleccionar autorizador"
            }
            options={filteredAuthorizers.map((a) => ({
              value: String(a.value),
              label: a.label,
            }))}
            disabled={!hasCompany}
            onAddNew={() => {
              if (requireCompany("autorizador")) setAuthorizerModalOpen(true);
            }}
            addTitle="Crear un autorizador nuevo en el catálogo"
            addLabel="Nuevo"
            addIcon={<UserPlus size={16} strokeWidth={2.25} aria-hidden />}
          />

          <FieldWithAdd
            label="Solicitante"
            name="requester"
            value={String(value.requester ?? "")}
            onChange={handleChange("requester")}
            placeholder={
              !hasCompany
                ? "Selecciona empresa en el paso 1"
                : filteredRequesters.length === 0
                  ? "Sin solicitantes — usa Nuevo"
                  : "Seleccionar solicitante"
            }
            options={filteredRequesters.map((r) => ({
              value: String(r.value),
              label: r.label,
            }))}
            disabled={!hasCompany}
            onAddNew={() => {
              if (requireCompany("solicitante")) setRequesterModalOpen(true);
            }}
            addTitle="Crear un solicitante nuevo en el catálogo"
            addLabel="Nuevo"
            addIcon={<UserPlus size={16} strokeWidth={2.25} aria-hidden />}
          />

          <FieldWithAdd
            label="Centro de costo"
            name="cost_center"
            value={String(value.cost_center ?? "")}
            onChange={handleChange("cost_center")}
            placeholder={
              !hasCompany
                ? "Selecciona empresa en el paso 1"
                : filteredCostCenters.length === 0
                  ? "Sin centros — usa Nuevo"
                  : "Seleccionar centro de costo"
            }
            options={filteredCostCenters.map((c) => ({
              value: String(c.value),
              label: c.label,
            }))}
            disabled={!hasCompany}
            onAddNew={() => {
              if (requireCompany("centro de costo")) setCostCenterModalOpen(true);
            }}
            addTitle="Crear un centro de costo nuevo en el catálogo"
            addLabel="Nuevo"
            addIcon={<Wallet size={16} strokeWidth={2.25} aria-hidden />}
          />
        </div>
      </div>

      {(fieldErrors?.detail || globalError) && (
        <div className="step3-error" role="alert">
          {fieldErrors?.detail || globalError}
        </div>
      )}

      <AuthorizerQuickAddModal
        isOpen={authorizerModalOpen}
        onClose={() => setAuthorizerModalOpen(false)}
        companyId={companyId}
        onCreated={handleAuthorizerCreated}
      />

      <RequesterQuickAddModal
        isOpen={requesterModalOpen}
        onClose={() => setRequesterModalOpen(false)}
        companyId={companyId}
        onCreated={handleRequesterCreated}
      />

      <CostCenterQuickAddModal
        isOpen={costCenterModalOpen}
        onClose={() => setCostCenterModalOpen(false)}
        companyId={companyId}
        onCreated={handleCostCenterCreated}
      />
    </div>
  );
};

export default ServiceFormStep3;
